import { useEffect, useRef } from 'react'
import { getFontByFamily, FONT_WEIGHT_VALUES } from '../data/fonts'

const LINK_ID_PREFIX = 'theme-fonts-'

// Track loaded fonts to avoid duplicate requests
const loadedFonts = new Set<string>()

/**
 * Build a Google Fonts URL for any font families.
 * Uses the curated THEME_FONTS list for known weights, falls back to
 * common weights (400;500;600;700) for fonts not in the curated list.
 */
function buildFontUrl(families: string[]): string | null {
  const params = families.map((family) => {
    const known = getFontByFamily(family)
    const weights = known
      ? known.weights.map((w) => FONT_WEIGHT_VALUES[w])
      : [400, 500, 600, 700]
    const encoded = family.replace(/ /g, '+')
    return `family=${encoded}:wght@${weights.join(';')}`
  })

  if (params.length === 0) return null
  return `https://fonts.googleapis.com/css2?${params.join('&')}&display=swap`
}

export function useFontLoader(families: string[]) {
  const previousFamiliesRef = useRef<string[]>([])

  useEffect(() => {
    // Filter to fonts that actually need loading
    const newFonts = families.filter((family) => {
      if (family === 'System UI') return false
      return !loadedFonts.has(family)
    })

    if (newFonts.length === 0) return

    const url = buildFontUrl(newFonts)
    if (!url) return

    // Create a unique ID for this link element
    const linkId = `${LINK_ID_PREFIX}${newFonts.join('-').replace(/\s+/g, '-')}`

    // Check if link already exists
    if (document.getElementById(linkId)) {
      newFonts.forEach((f) => loadedFonts.add(f))
      return
    }

    // Mark as loading immediately to prevent duplicates
    newFonts.forEach((f) => loadedFonts.add(f))

    // Create and inject the link element
    const link = document.createElement('link')
    link.id = linkId
    link.rel = 'stylesheet'
    link.href = url
    document.head.appendChild(link)

    previousFamiliesRef.current = families
  }, [families])
}

// Check if a font is loaded
export function isFontLoaded(family: string): boolean {
  if (family === 'System UI') return true
  return loadedFonts.has(family)
}
